import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowUpRight, ArrowDownRight, BarChart3, Loader2, TrendingUp
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Company domain mapping for logos (via Google Favicons)
const COMPANY_DOMAINS = {
  AAPL:'apple.com',GOOGL:'google.com',MSFT:'microsoft.com',AMZN:'amazon.com',TSLA:'tesla.com',META:'meta.com',
  NVDA:'nvidia.com',NFLX:'netflix.com',AMD:'amd.com',INTC:'intel.com',DIS:'disney.com',V:'visa.com',
  JPM:'jpmorganchase.com',WMT:'walmart.com',BAC:'bankofamerica.com',KO:'coca-cola.com',PEP:'pepsico.com',
  SPCX:'spacex.com',SPACEX:'spacex.com',
  MA:'mastercard.com',COST:'costco.com',CRM:'salesforce.com',ADBE:'adobe.com',PYPL:'paypal.com',
  UBER:'uber.com',ABNB:'airbnb.com',BA:'boeing.com',GS:'goldmansachs.com',MS:'morganstanley.com',
  BLK:'blackrock.com',T:'att.com',CSCO:'cisco.com',ORCL:'oracle.com',IBM:'ibm.com',QCOM:'qualcomm.com',
  NOW:'servicenow.com',COIN:'coinbase.com',PLTR:'palantir.com',SPOT:'spotify.com',CRWD:'crowdstrike.com',
  HD:'homedepot.com',UNH:'unitedhealthgroup.com',JNJ:'jnj.com',PFE:'pfizer.com',
  XOM:'exxonmobil.com',NKE:'nike.com',MCD:'mcdonalds.com',F:'ford.com',DAL:'delta.com',
  // Euronext
  MC:'lvmh.com',OR:'loreal.com',RMS:'hermes.com',TTE:'totalenergies.com',SAN:'sanofi.com',
  AI:'airliquide.com',SU:'se.com',BNP:'bnpparibas.com',AIR:'airbus.com',SAF:'safran-group.com',
  DSY:'3ds.com',KER:'kering.com',CAP:'capgemini.com',GLE:'societegenerale.com',ACA:'credit-agricole.com',
  ORA:'orange.com',ENGI:'engie.com',RNO:'renaultgroup.com',VIE:'veolia.com',CDI:'dior.com',
  DG:'vinci.com',CS:'axa.com',SGO:'saint-gobain.com',BN:'danone.com',ML:'michelin.com',
  HO:'thalesgroup.com',ALO:'alstom.com',CA:'carrefour.com',AM:'dassault-aviation.com',
};

const BRAND_COLORS = {
  AAPL:'#A2AAAD',GOOGL:'#4285F4',MSFT:'#00A4EF',AMZN:'#FF9900',TSLA:'#CC0000',META:'#0668E1',
  NVDA:'#76B900',NFLX:'#E50914',AMD:'#ED1C24',INTC:'#0071C5',DIS:'#0057A8',V:'#1A1F71',
  JPM:'#003A70',WMT:'#0071DC',BAC:'#012169',KO:'#F40009',PEP:'#004B93',MA:'#EB001B',
  SPCX:'#000000',SPACEX:'#000000',
  COST:'#E31837',CRM:'#00A1E0',ADBE:'#FF0000',PYPL:'#003087',UBER:'#000000',ABNB:'#FF5A5F',
  BA:'#0033A0',GS:'#6F9FD8',MS:'#002B5C',BLK:'#000000',COIN:'#0052FF',PLTR:'#101820',
  SPOT:'#1DB954',CRWD:'#F24B2F',HD:'#EE7125',NKE:'#111111',MCD:'#FFC72C',F:'#003478',
  MC:'#2B2B2B',OR:'#1A1A1A',RMS:'#F37021',TTE:'#FF0000',SAN:'#7B2D8E',AI:'#0055A4',
  SU:'#3DCD58',BNP:'#1B9E4B',AIR:'#00205B',SAF:'#0050A0',DSY:'#005386',KER:'#1D1D1B',
  CAP:'#0070AD',GLE:'#E30613',ACA:'#009639',ORA:'#FF7900',ENGI:'#00AAFF',RNO:'#FFD700',
  CDI:'#1A1A1A',VIE:'#00A0E1',
};

const getStockLogo = (symbol) => {
  const domain = COMPANY_DOMAINS[symbol];
  if (domain) return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  return null;
};

export default function ClientStockPortfolio() {
  const { token } = useAuth();
  const { primaryColor } = useTheme();
  const [holdings, setHoldings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stockPrices, setStockPrices] = useState({});
  const [euronextPrices, setEuronextPrices] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const retryCountRef = useRef(0);

  // Live price ticker state — simulates micro-fluctuations
  const [livePrices, setLivePrices] = useState({});
  const basePricesRef = useRef({});
  // Track previous prices for flash animation
  const [priceFlash, setPriceFlash] = useState({});

  const fetchData = useCallback(async () => {
    try {
      // Fetch portfolio data first (fast)
      const [portfolioRes, txnRes] = await Promise.all([
        axios.get(`${API}/stocks/my-portfolio`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/stocks/my-transactions?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setHoldings(portfolioRes.data.holdings || []);
      setTransactions(txnRes.data || []);
      setLoading(false);

      // Then fetch market prices (may return empty on first call - cache warming)
      const [usStocksRes, euronextRes] = await Promise.all([
        axios.get(`${API}/market/stocks`).catch(() => null),
        axios.get(`${API}/market/euronext`).catch(() => null),
      ]);

      const rate = usStocksRes?.data?.eur_rate || 1.08;
      const usPriceMap = {};
      (usStocksRes?.data?.prices || []).forEach(p => { usPriceMap[p.symbol] = p.price / rate; });
      setStockPrices(usPriceMap);

      const euPriceMap = {};
      (euronextRes?.data?.prices || []).forEach(p => { euPriceMap[p.symbol] = p.price; });
      setEuronextPrices(euPriceMap);

      // If prices came back empty, retry (cache is warming up on server ~20s)
      const hasUSPrices = Object.keys(usPriceMap).length > 0;
      const hasEUPrices = Object.keys(euPriceMap).length > 0;
      if (!hasUSPrices && !hasEUPrices && (portfolioRes.data.holdings || []).length > 0 && retryCountRef.current < 6) {
        retryCountRef.current += 1;
        setTimeout(fetchData, 5000);
        return;
      }

      // Initialize base prices for live ticker
      const bases = {};
      (portfolioRes.data.holdings || []).forEach(h => {
        const cp = h.category === 'euronext' ? (euPriceMap[h.symbol] || 0) : (usPriceMap[h.symbol] || 0);
        if (cp > 0) bases[h.symbol] = cp;
      });
      basePricesRef.current = bases;
      setLivePrices(prev => {
        const merged = { ...prev };
        Object.entries(bases).forEach(([k, v]) => { if (v > 0) merged[k] = v; });
        return merged;
      });
    } catch (err) { console.error(err); setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120000); return () => clearInterval(i); }, [fetchData]);

  // Live price ticker — micro-fluctuations every 2.5s
  useEffect(() => {
    if (!holdings.length) return;
    const tick = setInterval(() => {
      setLivePrices(prev => {
        const next = { ...prev };
        const flashes = {};
        holdings.forEach(h => {
          const base = basePricesRef.current[h.symbol] || 0;
          if (base <= 0) return;
          const existing = prev[h.symbol] || base;
          // Random walk ±0.08% for realism
          const jitter = existing * (Math.random() - 0.5) * 0.0016;
          const newPrice = existing + jitter;
          next[h.symbol] = newPrice;
          flashes[h.symbol] = newPrice > existing ? 'up' : newPrice < existing ? 'down' : '';
        });
        setPriceFlash(flashes);
        return next;
      });
    }, 2500);
    return () => clearInterval(tick);
  }, [holdings]);

  // Clear flash after 600ms
  useEffect(() => {
    if (Object.keys(priceFlash).length === 0) return;
    const t = setTimeout(() => setPriceFlash({}), 600);
    return () => clearTimeout(t);
  }, [priceFlash]);

  const getLivePrice = (symbol) => livePrices[symbol] || 0;

  const fmt = (v) => v?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00';

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div></DashboardLayout>;

  // Compute live values
  const enriched = holdings.map(h => {
    const currentPrice = getLivePrice(h.symbol);
    const value = h.quantity * currentPrice;
    const invested = h.quantity * h.buy_price;
    const pnl = currentPrice > 0 ? value - invested : 0;
    const pnlPct = invested > 0 && currentPrice > 0 ? (pnl / invested * 100) : 0;
    return { ...h, currentPrice, value, invested, pnl, pnlPct };
  });

  const totalValue = enriched.reduce((s, h) => s + h.value, 0);
  const totalInvested = enriched.reduce((s, h) => s + h.invested, 0);
  const totalPnl = totalValue - totalInvested;
  const totalPnlPct = totalInvested > 0 ? (totalPnl / totalInvested * 100) : 0;
  const isUpTotal = totalPnl >= 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="stock-portfolio-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Mes Actions</h1>
          <div className="flex items-center gap-3">
            {holdings.length > 0 && Object.keys(livePrices).length > 0 && (
              <span className="text-[10px] text-emerald-400 flex items-center gap-1 animate-pulse">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                EN DIRECT
              </span>
            )}
            {holdings.length > 0 && Object.keys(livePrices).length === 0 && (
              <span className="text-[10px] text-yellow-400 flex items-center gap-1 animate-pulse">
                <Loader2 className="w-3 h-3 animate-spin" />
                Chargement des cours...
              </span>
            )}
            {transactions.length > 0 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-sm underline"
                style={{ color: primaryColor }}
                data-testid="toggle-history-btn"
              >
                {showHistory ? 'Masquer l\'historique' : 'Voir l\'historique'}
              </button>
            )}
          </div>
        </div>

        {/* Portfolio Summary */}
        <Card className="bg-gradient-to-br from-card/90 to-card/60 backdrop-blur border-border/30 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Portefeuille Actions</p>
                <p className="text-4xl font-bold tabular-nums transition-all">{fmt(totalValue)} €</p>
                <div className="flex items-center gap-3 mt-2">
                  {totalInvested > 0 && totalValue > 0 && (
                    <span className={`text-sm font-semibold flex items-center gap-1 ${isUpTotal ? 'text-emerald-400' : 'text-red-400'}`}>
                      {isUpTotal ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      {isUpTotal ? '+' : ''}{fmt(totalPnl)} € ({totalPnlPct.toFixed(2)}%)
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">Investi : {fmt(totalInvested)} €</span>
                </div>
              </div>
              <div className="flex gap-6 text-center">
                <div>
                  <p className="text-2xl font-bold">{holdings.length}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Actions</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Holdings */}
        {holdings.length === 0 ? (
          <Card className="bg-card/80 border-border/30">
            <CardContent className="py-16 text-center">
              <BarChart3 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-lg text-muted-foreground">Aucune action dans votre portefeuille</p>
              <p className="text-sm text-muted-foreground mt-1">Contactez votre conseiller pour investir en bourse</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {enriched.map(h => {
              const isUp = h.pnl >= 0;
              const alloc = totalValue > 0 ? ((h.value / totalValue) * 100).toFixed(1) : 0;
              const logo = getStockLogo(h.symbol);
              const brandColor = BRAND_COLORS[h.symbol] || primaryColor;
              const flash = priceFlash[h.symbol];
              return (
                <Card key={h.id || h.symbol} className="bg-card/80 backdrop-blur border-border/30 hover:border-primary/30 transition-all group" data-testid={`stock-holding-${h.symbol}`}>
                  <CardContent className="p-0">
                    <div className="flex items-center">
                      {/* Left - stock info with logo */}
                      <div className="flex items-center gap-4 p-5 flex-1 min-w-0">
                        <div className="relative w-12 h-12 rounded-full flex items-center justify-center overflow-hidden shrink-0" style={{ backgroundColor: brandColor + '20', border: `2px solid ${brandColor}40` }}>
                          {logo ? (
                            <img src={logo} alt={h.symbol} className="w-8 h-8 object-contain" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                          ) : null}
                          <span className={`text-xs font-bold ${logo ? 'hidden' : 'flex'}`} style={{ color: brandColor, position: logo ? 'absolute' : 'relative', inset: 0, display: logo ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {h.symbol?.slice(0, 3)}
                          </span>
                          {logo && <span className="absolute inset-0 items-center justify-center text-xs font-bold" style={{ color: brandColor, display: 'none' }}>{h.symbol?.slice(0, 3)}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-lg">{h.symbol}</p>
                            <p className="text-xs text-muted-foreground truncate">{h.name}</p>
                            <Badge variant="outline" className="text-[9px] h-4">{h.category === 'euronext' ? 'EURONEXT' : 'US'}</Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-0.5">
                            <span className="text-sm font-mono text-muted-foreground">{h.quantity} actions</span>
                            {totalValue > 0 && <span className="text-xs text-muted-foreground">{alloc}%</span>}
                            <span className="text-[10px] text-muted-foreground">Achat : {fmt(h.buy_price)} €</span>
                          </div>
                        </div>
                      </div>

                      {/* Mid - cours actuel with live flash */}
                      <div className="text-right px-5 hidden md:block min-w-[150px]">
                        <p className="text-[10px] text-muted-foreground uppercase">Cours actuel</p>
                        <p className={`font-mono text-lg font-semibold tabular-nums transition-colors duration-300 ${flash === 'up' ? 'text-emerald-400' : flash === 'down' ? 'text-red-400' : 'text-foreground'}`}>
                          {h.currentPrice > 0 ? fmt(h.currentPrice) : '-'} €
                        </p>
                        {h.currentPrice > 0 && (
                          <span className={`text-xs font-semibold ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                            {isUp ? '+' : ''}{h.pnlPct.toFixed(2)}%
                          </span>
                        )}
                      </div>

                      {/* Right - value & P&L */}
                      <div className="text-right p-5 border-l border-border/20 min-w-[160px]">
                        <p className="text-[10px] text-muted-foreground uppercase">Ma position</p>
                        <p className={`font-bold text-lg tabular-nums transition-colors duration-300 ${flash === 'up' ? 'text-emerald-400' : flash === 'down' ? 'text-red-400' : ''}`}>
                          {h.currentPrice > 0 ? fmt(h.value) : fmt(h.invested)} €
                        </p>
                        {h.currentPrice > 0 && (
                          <span className={`text-sm font-semibold ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                            {isUp ? '+' : ''}{fmt(h.pnl)} €
                          </span>
                        )}
                        {h.currentPrice === 0 && (
                          <span className="text-xs text-muted-foreground">Cours indisponible</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Transaction History */}
        {showHistory && transactions.length > 0 && (
          <Card className="bg-card/80 border-border/30">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Historique des transactions
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50 text-muted-foreground">
                      <th className="text-left py-2 px-2">Date</th>
                      <th className="text-left py-2 px-2">Type</th>
                      <th className="text-left py-2 px-2">Action</th>
                      <th className="text-right py-2 px-2">Quantité</th>
                      <th className="text-right py-2 px-2">Prix unitaire</th>
                      <th className="text-right py-2 px-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(t => {
                      const tLogo = getStockLogo(t.symbol);
                      return (
                        <tr key={t.id} className="border-b border-border/20">
                          <td className="py-2 px-2 text-muted-foreground">
                            {new Date(t.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="py-2 px-2">
                            <Badge className={t.type === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                              {t.type === 'buy' ? 'Achat' : 'Vente'}
                            </Badge>
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-2">
                              {tLogo && <img src={tLogo} alt="" className="w-4 h-4 rounded-full" onError={e => { e.target.style.display = 'none'; }} />}
                              <span className="font-semibold">{t.symbol}</span>
                              <span className="text-xs text-muted-foreground">{t.name}</span>
                            </div>
                          </td>
                          <td className="text-right py-2 px-2 font-mono">{t.quantity}</td>
                          <td className="text-right py-2 px-2 font-mono">{fmt(t.price)} €</td>
                          <td className="text-right py-2 px-2 font-mono font-semibold">{fmt(t.total)} €</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
